import {VNode} from 'snabbdom';
import {AttrsSchema, Component, jsx} from 'panel';

import {ChartsContextRegistry} from 'common/charts/contexts/registry';
import {IronContextRegistry} from 'common/report/context-registry';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SegmentFilterController, SelectMode} from 'common/charts/contexts/segment-filter-controller';
import {DataTableEl} from 'common/components/data-table';
import {
  BodyRowHoverEvent,
  CellCoordinates,
  Columns,
  SelectionChangeEvent,
  TableRow,
  Theme,
  TrackName,
} from 'common/components/data-table/types';
import {propagateEvent} from 'common/util/dom';
import {reporter} from 'common/report/error-reporting';
import {panel2react} from 'common/util/react';

import css from './index.inline.styl';

export interface SegmentFilterDataTableJsxProps {
  attrs?: {
    collapsible?: boolean;
    theme?: Theme;
    'track-name': TrackName;
    'disable-scroll-control'?: boolean;
    'resizing-layout'?: boolean;
    'selected-cell'?: CellCoordinates;
  };
}
export class SegmentFilterDataTable extends Component<
  EmptyObj,
  SegmentFilterDataTableJsxProps['attrs'],
  any,
  any,
  ChartsContextRegistry & IronContextRegistry
> {
  static get attrsSchema(): AttrsSchema<SegmentFilterDataTableJsxProps['attrs']> {
    return {
      collapsible: `boolean`,
      theme: {
        type: `string`,
        enum: Object.values(Theme),
        default: Theme.Default,
      },
      'track-name': `string`,
      'disable-scroll-control': `boolean`,
      'resizing-layout': `boolean`,
      'selected-cell': `json`,
    };
  }

  get config() {
    return {
      useShadowDom: true,
      css,
      template: () => this.renderTemplate(),
      contexts: [`reportLayoutManager` as const, `segmentFilterController` as const],
    };
  }

  private get segmentFilterController(): SegmentFilterController {
    return this.getContext(`segmentFilterController`);
  }

  private getColumns(): Columns {
    return this.segmentFilterController.getSections()[0].tableColumns;
  }

  private getRows(): TableRow[] {
    const items = this.segmentFilterController.getItems(0);
    return items.flatMap((item) => {
      const mainRow = {
        ...item.tableRow,
        isSelected: this.segmentFilterController.isItemSelected(0, item.id),
        checkedColor: this.segmentFilterController.getItemColor(0, item.id),
      };
      if (item.comparisonTableRow) {
        const compareRow = {
          ...item.comparisonTableRow,
          isSelected: this.segmentFilterController.isItemSelected(0, item.id),
          checkedColor: this.segmentFilterController.getItemColor(0, item.id),
        };
        return [mainRow, compareRow];
      }
      return [mainRow];
    });
  }

  private hasComparisonRows(): boolean {
    const items = this.segmentFilterController.getItems(0);
    const hasComparisonRows = items.length > 0 ? !!items[0].comparisonTableRow : false;
    const comparisonRowPresenceConsistent =
      items.length <= 1 || items.every((item) => !!item.comparisonTableRow === hasComparisonRows);
    if (!comparisonRowPresenceConsistent) {
      throw new Error(`Some segment items have a comparisonTableRow while others do not`);
    }
    return hasComparisonRows;
  }

  private renderTemplate(): VNode {
    if (!this.segmentFilterController.getSections().length) {
      return <div />;
    }

    if (!this.segmentFilterController.tableSortConfig) {
      reporter.error(`SegmentFilterDataTable: sorting should be passed in via segmentFilterController.tableSortConfig`);
    }

    const hasComparison = this.hasComparisonRows();
    const rows = this.getRows();
    return (
      <DataTableEl
        attrs={{
          'multi-select': rows.length > 1 || this.attr(`theme`) !== Theme.Retention,
          theme: this.attr(`theme`),
          collapsible: this.attr(`collapsible`),
          'has-comparison': hasComparison,
          'track-name': this.attr(`track-name`),
          sorting: this.segmentFilterController.tableSortConfig,
          'selection-limit': this.segmentFilterController.maxNumSelections,
          'segmentation-limit': this.segmentFilterController.segmentationQueryLimit,
          'export-segmentation-limit': this.segmentFilterController.exportSegmentationQueryLimit,
          'disable-scroll-control': this.attr(`disable-scroll-control`),
          'resizing-layout': this.attr(`resizing-layout`),
          'selected-cell': this.attr(`selected-cell`),
        }}
        props={{
          dataTableParams: {
            columnGroups: this.getColumns(),
            rows,
            selectedRowCount: this.segmentFilterController.getSelectedCount(0),
            customTrackingProps: this.getContext(`reportLayoutManager`).getTrackingProps(),
          },
        }}
        on={{
          cellClick: (ev) => propagateEvent(this, ev),
          collapseChange: (ev) => propagateEvent(this, ev),
          selectionChange: (ev: SelectionChangeEvent) => {
            this.segmentFilterController.selectMode = SelectMode.Item;
            if (ev.detail.isToggleAll) {
              this.segmentFilterController.toggleOrderedTopItems(
                [
                  ev.detail.rows
                    .filter((_row, idx) => {
                      if (hasComparison) {
                        return idx % 2 === 0;
                      }
                      return true;
                    })
                    .map((row) => row.identifier),
                ],
                {isSearchMode: ev.detail.isSearchMode},
              );
            } else {
              ev.detail.changed.map(({row}) => {
                this.segmentFilterController.toggleItem(0, row.identifier);
              });
            }
          },
          sortChange: (ev) => propagateEvent(this, ev),
          tableScroll: (ev) => propagateEvent(this, ev),
          bodyRowHover: (ev: BodyRowHoverEvent) => {
            const {rowId} = ev.detail;
            this.segmentFilterController.legendHover(rowId ? {seriesId: rowId} : null);
          },
        }}
      />
    );
  }
}

export const SegmentFilterDataTableEl = defineAndMakeJsxFactory<SegmentFilterDataTableJsxProps>(
  `mp-segment-filter-data-table`,
  SegmentFilterDataTable,
);
export const SegmentFilterDataTableReact = panel2react<SegmentFilterDataTableJsxProps>(
  `mp-segment-filter-data-table`,
  SegmentFilterDataTable,
);
